/**
 * Stale-deploy recovery for lazy-loaded route chunks.
 *
 * When a new frontend is deployed, an already-loaded client still references the
 * previous build's hashed chunk filenames (e.g. /assets/ProfilePage-OLDHASH.js).
 * Those files no longer exist, so Firebase Hosting's SPA catch-all rewrite returns
 * index.html (HTML, status 200) for the missing .js. The dynamic import() then
 * rejects with a browser-specific message. We detect that family of errors and
 * reload once to pick up the fresh build.
 */

const RELOAD_GUARD_KEY = 'chunkReloadAttempt'
const RELOAD_GUARD_WINDOW_MS = 10_000

/**
 * Returns true when an error looks like a failed dynamic-import / chunk load
 * caused by a stale deploy. Covers Chrome, Firefox, Safari, and Vite's preload
 * helper, plus the MIME-type symptom from the SPA index.html fallback.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    (error instanceof Error ? error.message : typeof error === 'string' ? error : '') || ''

  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('Unable to preload CSS') ||
    message.includes('dynamically imported module') ||
    message.includes('MIME type') ||
    message.includes('text/html')
  )
}

/**
 * Reload the page once to recover from a stale-deploy chunk error. A timestamp
 * in sessionStorage prevents an infinite reload loop if the fresh build is still
 * genuinely broken — after the guard window, the error surfaces normally.
 */
export function reloadForStaleChunk(): boolean {
  try {
    const stored = sessionStorage.getItem(RELOAD_GUARD_KEY)
    const last = stored === null ? null : Number(stored)
    if (last !== null && Number.isFinite(last) && Date.now() - last < RELOAD_GUARD_WINDOW_MS) {
      // Already reloaded very recently — the new build is still failing.
      // Stop reloading so the user sees the real error instead of a loop.
      return false
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode / blocked) — reload anyway.
  }

  window.location.reload()
  return true
}

/**
 * Listen for Vite's preload-error event, which fires when __vitePreload fails
 * before React even attempts to render the lazy component. Reload to recover.
 * Call once at app startup.
 */
export function registerChunkReloadHandler(): void {
  if (typeof window === 'undefined') return

  window.addEventListener('vite:preloadError', (event) => {
    // Prevent Vite's default (which rethrows) so we can reload cleanly.
    event.preventDefault()
    reloadForStaleChunk()
  })
}
